<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import type { ClassPlannerItem } from '@/api'
import type { PaletteHue } from '@/design-system/components'

/**
 * Time-of-day week grid (mirrors the Android class week view): school-day columns,
 * working-day hour rows, lessons/assignments placed by time, and a red "now" line
 * on today's column when the visible week is the current one.
 */
export interface GridDay {
  date: Date
  dayName: string
  dayNumber: number
  dateString: string
  isToday: boolean
}
const props = defineProps<{
  days: GridDay[]
  itemsByDate: Map<string, ClassPlannerItem[]>
  hue: PaletteHue
  /** Teaching-day window (fractional hours) — the default visible span. Falls back to 9–16. */
  windowStartHour?: number
  windowEndHour?: number
}>()
const emit = defineEmits<{ 'item-click': [item: ClassPlannerItem]; add: [date: Date] }>()

const ROW_H = 52 // px per hour

function hoursOf(it: ClassPlannerItem): { start: number; end: number } {
  const s = new Date(it.dateTime)
  const start = s.getHours() + s.getMinutes() / 60
  const e = it.endDateTime ? new Date(it.endDateTime) : null
  const end = e ? e.getHours() + e.getMinutes() / 60 : start + 1
  return { start, end: Math.max(end, start + 0.5) }
}

// Grid hour range — spans the teaching-day window (from the school's Teaching hours setting),
// auto-expanding to fit any lesson that starts before / ends after it (mirrors Android).
const range = computed(() => {
  let min = Math.floor(props.windowStartHour ?? 9)
  let max = Math.ceil(props.windowEndHour ?? 16)
  for (const list of props.itemsByDate.values()) {
    for (const it of list) {
      if (it.type === 'Assignment') continue // assignments are due-markers, not timed blocks
      const { start, end } = hoursOf(it)
      min = Math.min(min, Math.floor(start))
      max = Math.max(max, Math.ceil(end))
    }
  }
  min = Math.max(0, min)
  max = Math.min(24, max)
  if (max <= min) max = Math.min(24, min + 1) // guard against an inverted/empty window
  return { start: min, end: max }
})
const gridStart = computed(() => range.value.start)
const hourLines = computed(() => {
  const out: number[] = []
  for (let h = range.value.start; h <= range.value.end; h++) out.push(h)
  return out
})
const rowCount = computed(() => range.value.end - range.value.start)
const gridHeight = computed(() => rowCount.value * ROW_H)

function fmtHour(h: number): string {
  return `${String(h).padStart(2, '0')}:00`
}
function fmtTime(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

interface Block {
  item: ClassPlannerItem
  top: number
  height: number
  timed: boolean
}
function blocksFor(dateString: string): Block[] {
  const list = props.itemsByDate.get(dateString) ?? []
  return list.map((item) => {
    if (item.type === 'Assignment') {
      const d = new Date(item.dateTime)
      const at = d.getHours() + d.getMinutes() / 60
      return { item, top: (at - range.value.start) * ROW_H, height: 22, timed: false }
    }
    const { start, end } = hoursOf(item)
    return {
      item,
      top: (start - range.value.start) * ROW_H,
      height: (end - start) * ROW_H,
      timed: true,
    }
  })
}

// Current-time line
const now = ref(new Date())
let timer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  timer = setInterval(() => (now.value = new Date()), 60_000)
})
onUnmounted(() => {
  if (timer) clearInterval(timer)
})
const nowFrac = computed(() => now.value.getHours() + now.value.getMinutes() / 60)
const nowTop = computed(() => (nowFrac.value - range.value.start) * ROW_H)
const showNow = computed(
  () => props.days.some((d) => d.isToday) && nowFrac.value >= range.value.start && nowFrac.value <= range.value.end,
)
const todayIndex = computed(() => props.days.findIndex((d) => d.isToday))
</script>

<template>
  <div class="wg">
    <!-- Day headers -->
    <div class="wg__head" :style="{ gridTemplateColumns: `48px repeat(${days.length}, minmax(0, 1fr))` }">
      <div class="wg__corner" />
      <div v-for="d in days" :key="d.dateString" class="wg__dayhead" :class="{ 'wg__dayhead--today': d.isToday }">
        <span class="wg__dayname">{{ d.dayName }}</span>
        <span class="wg__daynum" :style="d.isToday ? { background: hue.solid, color: '#fff' } : undefined">{{ d.dayNumber }}</span>
      </div>
    </div>

    <!-- Grid body -->
    <!-- +8px so the final hour label (centred on the bottom grid line) isn't clipped -->
    <div class="wg__body" :style="{ gridTemplateColumns: `48px repeat(${days.length}, minmax(0, 1fr))`, height: gridHeight + 8 + 'px' }">
      <!-- hour gutter -->
      <div class="wg__gutter">
        <span
          v-for="h in hourLines"
          :key="h"
          class="wg__hourlabel"
          :style="{ top: (h - gridStart) * ROW_H + 'px' }"
        >{{ fmtHour(h) }}</span>
      </div>

      <!-- day columns -->
      <div
        v-for="(d, di) in days"
        :key="d.dateString"
        class="wg__col"
        :class="{ 'wg__col--today': d.isToday }"
        @click="emit('add', d.date)"
      >
        <div v-for="n in rowCount" :key="n" class="wg__cell" :style="{ height: ROW_H + 'px' }" />

        <button
          v-for="(b, bi) in blocksFor(d.dateString)"
          :key="bi"
          type="button"
          class="wg__block"
          :class="{ 'wg__block--due': !b.timed }"
          :style="
            b.timed
              ? { top: b.top + 'px', height: b.height + 'px', background: hue.soft, borderColor: hue.solid, color: hue.ink }
              : { top: b.top + 'px' }
          "
          @click.stop="emit('item-click', b.item)"
        >
          <template v-if="b.timed">
            <span class="wg__block-title">{{ b.item.title || 'Lesson' }}</span>
            <span class="wg__block-meta">{{ fmtTime(b.item.dateTime) }}<template v-if="b.item.room"> · {{ b.item.room }}</template></span>
          </template>
          <template v-else>
            <span class="wg__due-dot" :style="{ background: hue.solid }" />{{ b.item.title || 'Assignment' }} due
          </template>
        </button>

        <!-- now line -->
        <div v-if="showNow && di === todayIndex" class="wg__now" :style="{ top: nowTop + 'px' }">
          <span class="wg__now-dot" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wg {
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--surface);
  font-family: var(--font-sans);
}
.wg__head {
  display: grid;
  border-bottom: 1px solid var(--outline-variant);
}
.wg__corner {
  border-right: 1px solid var(--outline-variant);
}
.wg__dayhead {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 8px 4px;
  border-right: 1px solid var(--outline-variant);
}
.wg__dayhead:last-child {
  border-right: 0;
}
.wg__dayname {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ink-400);
}
.wg__daynum {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.wg__body {
  display: grid;
  position: relative;
}
.wg__gutter {
  position: relative;
  border-right: 1px solid var(--outline-variant);
}
.wg__hourlabel {
  position: absolute;
  right: 6px;
  transform: translateY(-50%);
  font-size: 10px;
  color: var(--ink-400);
  font-variant-numeric: tabular-nums;
}
.wg__col {
  position: relative;
  border-right: 1px solid var(--outline-variant);
}
.wg__col:last-child {
  border-right: 0;
}
.wg__col--today {
  background: color-mix(in srgb, var(--canvas) 55%, transparent);
}
.wg__cell {
  border-bottom: 1px solid var(--hairline, var(--outline-variant));
}
.wg__block {
  position: absolute;
  left: 3px;
  right: 3px;
  border: 1px solid;
  border-left-width: 3px;
  border-radius: 6px;
  padding: 3px 6px;
  text-align: left;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.wg__block-title {
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wg__block-meta {
  font-size: 10px;
  opacity: 0.85;
  font-variant-numeric: tabular-nums;
}
.wg__block--due {
  left: 3px;
  right: 3px;
  height: auto;
  border: 0;
  background: transparent;
  color: var(--ink-500);
  font-size: 10.5px;
  font-weight: 500;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 2px 4px;
}
.wg__due-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.wg__now {
  position: absolute;
  left: 0;
  right: 0;
  height: 0;
  border-top: 2px solid #ef4444;
  z-index: 2;
  pointer-events: none;
}
.wg__now-dot {
  position: absolute;
  left: -4px;
  top: -4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
}
</style>
